/**
 * A quick look at a folder (`?peek=dir_…`), as an item's: its name, where it is, who sees it and what it holds; then
 * what's in it as its page lists it (a table or cards, each looked at in turn from there); Open goes to its page.
 */
import { useEffect, useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { FolderList, PageItemRow, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { useEntities } from "../../lib/data.ts";
import { folderHref, whatInside } from "../../lib/folders.ts";
import { formatInstant, formatRelative } from "../../lib/format.ts";
import { spaceApi } from "../../lib/paths.ts";
import { useSpacePeople } from "../../lib/people.ts";
import { useResource } from "../../lib/resource.ts";
import { canEdit } from "../../lib/roles.ts";
import { useStored } from "../../lib/storage.ts";
import { Button, Link } from "../../ui/Button.tsx";
import { FolderCommentCount } from "../../ui/counts.tsx";
import { FolderIcon } from "../../ui/icons.tsx";
import { PeekFrame } from "../../ui/PeekFrame.tsx";
import { buttonClass, LINK } from "../../ui/styles.ts";
import { Table } from "../../ui/Table.tsx";
import { ViewToggle } from "../../ui/ViewToggle.tsx";
import { VisibilityMark } from "../item/visibility.tsx";
import { ItemCards } from "../items/cards.tsx";
import { columnsBetween } from "../items/columns.ts";
import { ItemsHead, Rows } from "../items/rows.tsx";
import { Breadcrumb } from "./breadcrumb.tsx";
import { FolderCards } from "./cards.tsx";
import { DROP_AROUND, useDropInto } from "./drop.ts";
import { useFolder } from "./listing.ts";
import { FolderRows } from "./rows.tsx";

/** The items a look lists at first, and more each time asked (up to the API's page size; the rest on its page). */
const LOOK_ITEMS = 50;
const MOST = 200;

/** How a folder's look lists what's in it: as its page does (table or cards), remembered in this browser. */
const VIEW_KEY = "revisa.peek.folderView";

/**
 * What's in the folder, as its page lists it: a table (its subfolders first, open in place, then its items; sorted by
 * their heads) or cards, the first items and more on asking; empty, said.
 */
function Contents({ space, id, name }: { space: SpaceOut; id: string; name: string }) {
  const editable = canEdit(space);
  // The folder takes what's dropped on its look (from it, from the page beside it), as a list's level does.
  const here = useDropInto(space, id, name, !editable);
  const [view, setView] = useStored<"table" | "cards">(VIEW_KEY, (raw) => (raw === "cards" ? "cards" : "table"));
  const [sort, setSort] = useState("title");
  const [limit, setLimit] = useState(LOOK_ITEMS);
  const entities = useEntities(space).data ?? [];
  const members = useSpacePeople(space);
  const subs = useResource(`look-subs:${space.id}:${id}`, (s) =>
    api.get<FolderList>(spaceApi(space, "/folders"), { parent: id }, s),
  );
  const items = useResource(`look-items:${space.id}:${id}:${sort}:${limit}`, (s) =>
    api.get<PageItemRow>(spaceApi(space, "/items"), { folder: id, sort, limit }, s),
  );
  if (subs.data === undefined || items.data === undefined) {
    return null;
  }
  const folders = subs.data.data;
  const page = items.data;
  if (folders.length === 0 && page.data.length === 0) {
    return (
      <div class="p-4">
        <p class={`rounded-lg p-4 text-sm text-zinc-500 ${here.over ? DROP_AROUND : ""}`} {...here.props}>
          {t("folders.empty")}
        </p>
      </div>
    );
  }
  const env = { space, entities, filters: {}, onChange: () => items.reload() };
  return (
    <div class="flex flex-col gap-3 p-4">
      <ViewToggle views={["table", "cards"]} view={view} onView={(v) => setView(v === "cards" ? "cards" : "table")} />
      <div class={`rounded-lg ${here.over ? DROP_AROUND : ""}`} {...here.props}>
        {view === "cards" ? (
          <>
            <FolderCards space={space} folders={folders} draggable={editable} />
            <ItemCards
              space={space}
              page={page}
              entity={undefined}
              entities={entities}
              members={members}
              draggable={editable}
            />
          </>
        ) : (
          <Table>
            <ItemsHead entity={undefined} sort={sort} onSort={setSort} />
            <FolderRows env={env} folders={folders} between={columnsBetween(undefined, false)} movable={editable} />
            <Rows
              space={space}
              page={page}
              entity={undefined}
              entities={entities}
              members={members}
              draggable={editable}
            />
          </Table>
        )}
      </div>
      {page.next && limit < MOST ? (
        <Button variant="ghost" class="self-start" onClick={() => setLimit(Math.min(limit + LOOK_ITEMS, MOST))}>
          {t("items.tree.more")}
        </Button>
      ) : page.next ? (
        <Link href={folderHref(space, id)} class={`${LINK} text-sm`}>
          {t("peek.open")}
        </Link>
      ) : null}
    </div>
  );
}

/** The folder looked at; it slides in once it's there, and out when closed. */
export function FolderLook(p: { space: SpaceOut; id: string; leaving: boolean; onGone: () => void }) {
  const { space, id, leaving, onGone } = p;
  const r = useFolder(space, id);
  const folder = r.data;
  const [ready, setReady] = useState(false);
  const show = ready || folder !== undefined || r.error !== undefined;
  if (show && !ready) {
    setReady(true);
  }
  useEffect(() => {
    if (!show && leaving) {
      onGone();
    }
  }, [show, leaving, onGone]);
  if (!show) {
    return null;
  }
  const what = folder ? whatInside(folder.counts) : "";
  return (
    <PeekFrame
      leaving={leaving}
      onGone={onGone}
      label={folder?.name ?? t("common.loading")}
      title={
        folder ? (
          <>
            {/* As the folder's page heads it. */}
            <div class="flex items-center gap-2">
              <FolderIcon size={20} class="shrink-0 text-zinc-500" />
              <h2 class="line-clamp-2 min-w-0 font-medium [overflow-wrap:anywhere] text-zinc-900">{folder.name}</h2>
              <VisibilityMark item={folder} members={undefined} size={16} />
            </div>
            <Breadcrumb space={space} path={folder.path} root={true} small={true} class="mt-1" />
            <p class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500">
              {/* Empty: said below, where its contents would be. */}
              {what ? <span>{what}</span> : null}
              <span title={formatInstant(folder.updated_at)}>
                {t("items.cardUpdated", { when: formatRelative(folder.updated_at) })}
              </span>
              <FolderCommentCount space={space} id={folder.id} />
            </p>
          </>
        ) : null
      }
      actions={
        <Link href={folderHref(space, id)} class={buttonClass("secondary", "sm")}>
          {t("peek.open")}
        </Link>
      }
    >
      {folder ? (
        <Contents space={space} id={id} name={folder.name} />
      ) : r.error ? (
        <p class="p-4 text-sm">{r.error.message}</p>
      ) : null}
    </PeekFrame>
  );
}
